import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SalovareLoader from '../common/SalovareLoader';
import { fieldLabelSx } from '../common/formFieldStyles';
import employeePaymentService from '../../services/employeePaymentService';
import { PAY_DAY_DEFAULT, PAY_DAY_MAX, PAY_DAY_MIN } from '../../constants/EmployeePaymentModes';
import {
  MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL,
  DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL,
} from '../../constants/MembershipRevenueDistributionModel';
import { useToast } from '../../context/ToastContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';

const payDayOptions = Array.from({ length: PAY_DAY_MAX - PAY_DAY_MIN + 1 }, (_, index) => PAY_DAY_MIN + index);

const buildTextFieldSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  lineHeight: 'normal',
  minHeight: 48,
  '&:focus': {
    borderColor: hasError ? '#f44336' : BRAND.accent,
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
  },
});

const accordionStyles = {
  backgroundColor: '#ffffff',
  borderRadius: '8px !important',
  border: `1px solid ${BRAND.line}`,
  boxShadow: 'none',
  '&:before': { display: 'none' },
  overflow: 'hidden',
};

const MembershipRevenueSettingsCard = ({
  storeId,
  canEdit,
  asTabPanel = false,
  initialSettings = null,
  settingsLoading = false,
  settingsError = null,
  currency = DEFAULT_CURRENCY,
  onSettingsSaved,
}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const isManaged = asTabPanel;

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(!isManaged);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [storeMarginPercent, setStoreMarginPercent] = useState('0');
  const [membershipSharePayDayOfMonth, setMembershipSharePayDayOfMonth] = useState(PAY_DAY_DEFAULT);
  const [distributionModel, setDistributionModel] = useState(DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL);
  const [nextPayDate, setNextPayDate] = useState(null);
  const [pendingUndistributedBarberPoolAmount, setPendingUndistributedBarberPoolAmount] = useState(0);

  const displayLoading = isManaged ? settingsLoading : loading;
  const displayError = isManaged ? settingsError : loadError;

  const marginValue = useMemo(() => Number.parseFloat(storeMarginPercent), [storeMarginPercent]);
  const isMarginValid = Number.isFinite(marginValue) && marginValue >= 0 && marginValue <= 100;
  const showMarginError = storeMarginPercent.trim() !== '' && !isMarginValid;

  const loadSettings = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`membership-revenue-settings-${storeId}`, async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const settings = await employeePaymentService.getMembershipRevenueSettings(storeId);
        setStoreMarginPercent(String(settings.storeMarginPercent ?? 0));
        setMembershipSharePayDayOfMonth(settings.membershipSharePayDayOfMonth ?? PAY_DAY_DEFAULT);
        setDistributionModel(settings.distributionModel ?? DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL);
        setNextPayDate(settings.nextPayDate ?? null);
        setPendingUndistributedBarberPoolAmount(settings.pendingUndistributedBarberPoolAmount ?? 0);
      } catch (error) {
        console.error('Failed to load membership revenue settings:', error);
        setLoadError(t('membershipPlansManagement.revenueSettings.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, t]);

  useEffect(() => {
    if (!isManaged || !initialSettings) {
      return;
    }

    setStoreMarginPercent(String(initialSettings.storeMarginPercent ?? 0));
    setMembershipSharePayDayOfMonth(initialSettings.membershipSharePayDayOfMonth ?? PAY_DAY_DEFAULT);
    setDistributionModel(initialSettings.distributionModel ?? DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL);
    setNextPayDate(initialSettings.nextPayDate ?? null);
    setPendingUndistributedBarberPoolAmount(initialSettings.pendingUndistributedBarberPoolAmount ?? 0);
  }, [initialSettings, isManaged]);

  useEffect(() => {
    if (isManaged) {
      return;
    }

    loadSettings();
  }, [isManaged, loadSettings]);

  const handleSave = async () => {
    if (!isMarginValid) {
      showError(t('membershipPlansManagement.revenueSettings.error.marginRange'));
      return;
    }

    try {
      setSaving(true);
      await employeePaymentService.updateMembershipRevenueSettings(storeId, {
        storeMarginPercent: marginValue,
        membershipSharePayDayOfMonth: Number(membershipSharePayDayOfMonth),
        distributionModel,
      });
      const refreshed = await employeePaymentService.getMembershipRevenueSettings(storeId);
      setStoreMarginPercent(String(refreshed.storeMarginPercent ?? 0));
      setMembershipSharePayDayOfMonth(refreshed.membershipSharePayDayOfMonth ?? PAY_DAY_DEFAULT);
      setDistributionModel(refreshed.distributionModel ?? DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL);
      setNextPayDate(refreshed.nextPayDate ?? null);
      setPendingUndistributedBarberPoolAmount(refreshed.pendingUndistributedBarberPoolAmount ?? 0);
      onSettingsSaved?.(refreshed);
      showSuccess(t('membershipPlansManagement.revenueSettings.success.saved'));
      if (!asTabPanel) {
        setExpanded(false);
      }
    } catch (error) {
      console.error('Failed to save membership revenue settings:', error);
      showError(t('membershipPlansManagement.revenueSettings.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const settingsForm = displayLoading ? (
    <SalovareLoader size="small" center sx={{ py: 2 }} />
  ) : (
    <Stack spacing={2}>
      {displayError && <Alert severity="error">{displayError}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('membershipPlansManagement.revenueSettings.storeMarginPercent')}
          </Typography>
          <Box
            component="input"
            type="number"
            aria-label={t('membershipPlansManagement.revenueSettings.storeMarginPercent')}
            aria-invalid={showMarginError}
            value={storeMarginPercent}
            onChange={(event) => setStoreMarginPercent(event.target.value)}
            disabled={!canEdit}
            min={0}
            max={100}
            step="0.01"
            sx={buildTextFieldSx(showMarginError)}
          />
          {showMarginError ? (
            <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
              {t('membershipPlansManagement.revenueSettings.error.marginRange')}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <SalovareSelectField
            label={t('membershipPlansManagement.revenueSettings.membershipSharePayDay')}
            value={membershipSharePayDayOfMonth}
            onChange={(event) => setMembershipSharePayDayOfMonth(event.target.value)}
            disabled={!canEdit}
          >
            {payDayOptions.map((day) => (
              <MenuItem key={day} value={day}>
                {t('membershipPlansManagement.revenueSettings.payDayOption', { day })}
              </MenuItem>
            ))}
          </SalovareSelectField>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}
          >
            <EventIcon sx={{ fontSize: 18, mt: 0.25, color: BRAND.accentText, flexShrink: 0 }} />
            {t('membershipPlansManagement.revenueSettings.payDayHint')}
          </Typography>
        </Box>
      </Box>

      <Box>
        <SalovareSelectField
          label={t('membershipPlansManagement.revenueSettings.distributionModel')}
          value={distributionModel}
          onChange={(event) => setDistributionModel(event.target.value)}
          disabled={!canEdit}
        >
          <MenuItem value={MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.MonthlyPot}>
            {t('membershipPlansManagement.revenueSettings.distributionModelOptions.monthlyPot')}
          </MenuItem>
          <MenuItem value={MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.PeriodUsageProportional}>
            {t('membershipPlansManagement.revenueSettings.distributionModelOptions.periodUsage')}
          </MenuItem>
        </SalovareSelectField>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {distributionModel === MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL.PeriodUsageProportional
            ? t('membershipPlansManagement.revenueSettings.distributionModelHints.periodUsage')
            : t('membershipPlansManagement.revenueSettings.distributionModelHints.monthlyPot')}
        </Typography>
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t('membershipPlansManagement.revenueSettings.distributionModelSwitchWarning', {
            date: formatCalendarDate(nextPayDate),
          })}
        </Alert>
        {pendingUndistributedBarberPoolAmount > 0 ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {t('membershipPlansManagement.revenueSettings.pendingUndistributed', {
              amount: formatCurrency(pendingUndistributedBarberPoolAmount, currency),
              date: formatCalendarDate(nextPayDate),
            })}
          </Alert>
        ) : null}
      </Box>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryActionButton onClick={handleSave} disabled={saving || Boolean(displayError) || !isMarginValid}>
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </Box>
      )}
    </Stack>
  );

  if (asTabPanel) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 0.5 }}>
            {t('membershipPlansManagement.revenueSettings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('membershipPlansManagement.revenueSettings.subtitle')}
          </Typography>
        </Box>
        {settingsForm}
      </Box>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      disableGutters
      elevation={0}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={accordionStyles}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: BRAND.accentText }} />}
        sx={{
          px: { xs: 2, sm: 2.5 },
          minHeight: 56,
          '& .MuiAccordionSummary-content': { my: 1.25 },
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
            {t('membershipPlansManagement.revenueSettings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('membershipPlansManagement.revenueSettings.subtitle')}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: { xs: 2, sm: 2.5 } }}>
        {settingsForm}
      </AccordionDetails>
    </Accordion>
  );
};

export default MembershipRevenueSettingsCard;
